/**
 * Pure helpers that read the asset contract out of a parsed GLB scene graph (asset contract §2.4-§2.6, §3.4).
 * Node-safe: no DOM, no renderer. Gameplay-owned; signatures frozen for M1.
 */
import { Matrix4, Object3D, Vector3, type BufferAttribute, type BufferGeometry, type InterleavedBufferAttribute, type Mesh } from 'three/webgpu'
import { COLLIDER_NAME, DECAL_PATCH_NAME, LOD_NAME, SOCKET_NAME, type ColliderEntry } from '../data/assetSchema'

/** Collision shapes in asset space (metres). Built once per model at load, before anything renders. */
export type ColliderData =
  | { kind: 'ball'; radius: number }
  | { kind: 'convexHulls'; hulls: Float32Array[] } // asset-space xyz triplets, one array per COL_ node
  | { kind: 'trimesh'; vertices: Float32Array; indices: Uint32Array }

/** `decalPatch`: a baked hull's `DCL_` patch (Meshy-ships plan C6); its runtime use lands with card W1-2. */
export type ContractNodeKind = 'lod' | 'collider' | 'socket' | 'decalPatch' | 'reserved' | 'plain'

const RESERVED = /^MOV_[A-Z0-9_]+$/
const LOOKS_LIKE_CONTRACT = /^[A-Z]{2,}_/

/** Classifies a root-level node name. Throws on a name that looks like a contract prefix but is not one. */
export function classifyNode(name: string): ContractNodeKind {
  if (LOD_NAME.test(name)) return 'lod'
  if (COLLIDER_NAME.test(name)) return 'collider'
  if (SOCKET_NAME.test(name)) return 'socket'
  if (DECAL_PATCH_NAME.test(name)) return 'decalPatch'
  if (RESERVED.test(name)) return 'reserved'
  if (LOOKS_LIKE_CONTRACT.test(name)) throw new Error(`Unknown contract node prefix: ${name}`)
  return 'plain'
}

/** LOD0..LODn root children in order. Throws when a level is missing. */
export function collectLods(root: Object3D): Object3D[] {
  const levels: Object3D[] = []
  for (const child of root.children) {
    if (classifyNode(child.name) === 'lod') levels[Number(child.name.slice(3))] = child
  }
  for (let i = 0; i < levels.length; i++) {
    if (!levels[i]) throw new Error(`LOD${i} missing`)
  }
  if (levels.length === 0) throw new Error('No LOD0 node')
  return levels
}

/**
 * Throws naming the model when any `COL_` node is left anywhere under `root` (contract §3.4: collision
 * geometry never reaches the GPU). The loader runs it on every instance in development builds.
 */
export function assertNoColliderNodes(root: Object3D, modelId: string): void {
  root.traverse((o) => {
    if (o.name.startsWith('COL_')) throw new Error(`${modelId}: collider node ${o.name} survived into an instance`)
  })
}

/** Socket empties at the root, by name. */
export function collectSockets(root: Object3D): Map<string, Object3D> {
  const sockets = new Map<string, Object3D>()
  for (const child of root.children) {
    if (classifyNode(child.name) === 'socket') sockets.set(child.name, child)
  }
  return sockets
}

type PositionAttribute = BufferAttribute | InterleavedBufferAttribute

function meshesOf(node: Object3D): Mesh[] {
  const out: Mesh[] = []
  node.traverse((o) => {
    if ((o as Mesh).isMesh) out.push(o as Mesh)
  })
  return out
}

const tmp = new Vector3()
const rel = new Matrix4()

function appendPositions(geometry: BufferGeometry, toAsset: Matrix4, out: number[]): number {
  const pos = geometry.getAttribute('position') as PositionAttribute | undefined
  if (!pos) return 0
  for (let i = 0; i < pos.count; i++) {
    // fromBufferAttribute de-normalizes quantized data; the matrix undoes gltfpack's dequantization transform.
    tmp.fromBufferAttribute(pos, i).applyMatrix4(toAsset)
    out.push(tmp.x, tmp.y, tmp.z)
  }
  return pos.count
}

/**
 * Reads the COL_ nodes named by the manifest entry into asset-space collider data, then detaches
 * them and disposes their geometry so they never reach the GPU (contract §3.4).
 */
export function extractColliderData(root: Object3D, entry: ColliderEntry | null): ColliderData | null {
  const colNodes = root.children.filter((c) => classifyNode(c.name) === 'collider')
  let result: ColliderData | null = null
  if (entry) {
    root.updateMatrixWorld(true)
    const rootInverse = new Matrix4().copy(root.matrixWorld).invert()
    const find = (name: string): Object3D => {
      const node = colNodes.find((c) => c.name === name)
      if (!node) throw new Error(`Collider node ${name} missing from GLB`)
      return node
    }
    if (entry.kind === 'ball') {
      result = { kind: 'ball', radius: entry.radius }
    } else if (entry.kind === 'convexHulls') {
      const hulls: Float32Array[] = []
      for (const name of entry.nodes) {
        const points: number[] = []
        for (const mesh of meshesOf(find(name))) {
          rel.multiplyMatrices(rootInverse, mesh.matrixWorld)
          appendPositions(mesh.geometry, rel, points)
        }
        if (points.length < 12) throw new Error(`Collider ${name} has fewer than 4 points`)
        hulls.push(new Float32Array(points))
      }
      result = { kind: 'convexHulls', hulls }
    } else {
      const vertices: number[] = []
      const indices: number[] = []
      for (const name of entry.nodes) {
        for (const mesh of meshesOf(find(name))) {
          rel.multiplyMatrices(rootInverse, mesh.matrixWorld)
          const base = vertices.length / 3
          const count = appendPositions(mesh.geometry, rel, vertices)
          const index = mesh.geometry.getIndex()
          if (index) {
            for (let i = 0; i < index.count; i++) indices.push(base + index.getX(i))
          } else {
            for (let i = 0; i < count; i++) indices.push(base + i)
          }
        }
      }
      result = { kind: 'trimesh', vertices: new Float32Array(vertices), indices: new Uint32Array(indices) }
    }
  }
  for (const node of colNodes) {
    node.removeFromParent()
    for (const mesh of meshesOf(node)) mesh.geometry.dispose()
  }
  return result
}
